import { useCallback, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

const scrollAmount = 200;
const scrollEndThreshold = 1;

export const useVerticalScroll = (showScrollArrows: boolean, itemCount: number) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const updateArrowVisibility = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const { clientWidth, scrollWidth, scrollLeft } = el;
    setShowLeftArrow(scrollLeft > scrollEndThreshold);
    setShowRightArrow(clientWidth + scrollLeft < scrollWidth - scrollEndThreshold);
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (!showScrollArrows) return;
    updateArrowVisibility();

    const el = listRef.current;
    if (!el) return;

    const resizeObserver = new ResizeObserver(() => {
      updateArrowVisibility();
    });
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [showScrollArrows, itemCount, updateArrowVisibility]);

  const handleScrollLeft = useCallback(() => {
    const el = listRef.current;
    if (el) {
      el.scrollTo({ left: Math.max(0, el.scrollLeft - scrollAmount), behavior: 'smooth' });
    }
  }, []);

  const handleScrollRight = useCallback(() => {
    const el = listRef.current;
    if (el) {
      el.scrollTo({
        left: Math.min(el.scrollWidth - el.clientWidth, el.scrollLeft + scrollAmount),
        behavior: 'smooth',
      });
    }
  }, []);

  return {
    listRef,
    showLeftArrow,
    showRightArrow,
    handleScrollLeft,
    handleScrollRight,
    updateArrowVisibility,
  };
};
